import React, { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { useModulePermissions } from "../../hooks/useNavigationPermissions";
import {
  NestedGroupConfig,
  getVisibleNestedItems,
  isSuperAdmin,
  getUserModules,
} from "../../utils/navigationUtils";
import { nestedGroups } from "../../config/navigation";
import Loader from "../common/Loader";

interface NestedGroupIndexProps {
  groupId: string;
}

/**
 * Component that redirects to the first available route in a nested group
 * based on user permissions. This ensures users are redirected to a route
 * they have access to, rather than a hardcoded route.
 */
const NestedGroupIndex: React.FC<NestedGroupIndexProps> = ({ groupId }) => {
  const { profile } = useAuth();

  const group = useMemo(() => {
    return nestedGroups.find((g) => g.id === groupId);
  }, [groupId]);

  const isSuperAdminUser = useMemo(
    () => isSuperAdmin(profile?.role),
    [profile?.role],
  );

  const userModules = useMemo(() => {
    const permissions: string[] =
      profile && Array.isArray(profile.permissions) ? profile.permissions : [];
    return getUserModules(permissions);
  }, [profile]);

  // Get unique modules for this group
  const uniqueModules = useMemo(() => {
    if (!group) return [];
    return Array.from(new Set(group.items.map((item) => item.module)));
  }, [group]);

  // Get permissions for all modules in this group
  const modulePermissions = useModulePermissions(uniqueModules);

  // Get visible items based on permissions
  const filteredVisibleItems = useMemo(() => {
    if (!group) return [];
    const items = getVisibleNestedItems(group, userModules, isSuperAdminUser);
    return items.filter((item) => {
      // Check if user has view permission for this module
      return modulePermissions[item.module] ?? false;
    });
  }, [group, userModules, isSuperAdminUser, modulePermissions]);

  if (!profile) {
    return <Loader fullScreen />;
  }

  if (!group) {
    return <Navigate to="/dashboard" replace />;
  }

  // Redirect to the first available route
  if (filteredVisibleItems.length > 0) {
    return <Navigate to={filteredVisibleItems[0].route} replace />;
  }

  // If no items are available, redirect to unauthorized
  return <Navigate to="/unauthorized" replace />;
};

export default NestedGroupIndex;
